import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { INDUSTRIES as INDUSTRY_OPTIONS } from '../data/dropdownOptions';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Plus, FileText, Send, Trash2, Pencil, Eye, Sparkles, Settings2,
  Upload, Palette, Building2, Mail, User, DollarSign, Clock,
  CheckCircle2, XCircle, ChevronDown, ChevronUp, Copy, Save,
  Loader2, LayoutTemplate, Image as ImageIcon, Type, ArrowRight, Link2
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: FileText },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  { value: 'viewed', label: 'Viewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  { value: 'accepted', label: 'Accepted', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
];

const SECTION_META = [
  { key: 'hook', label: 'Executive Summary', icon: Sparkles, color: '#8B5CF6' },
  { key: 'pain_points', label: 'Client Challenges', icon: XCircle, color: '#EF4444' },
  { key: 'value_proposition', label: 'Our Solution', icon: CheckCircle2, color: '#10B981' },
  { key: 'scope_of_work', label: 'Scope of Work', icon: FileText, color: '#3B82F6' },
  { key: 'deliverables', label: 'Deliverables', icon: CheckCircle2, color: '#F59E0B' },
  { key: 'pricing', label: 'Investment', icon: DollarSign, color: '#8B5CF6' },
  { key: 'timeline', label: 'Timeline', icon: Clock, color: '#06B6D4' },
  { key: 'social_proof', label: 'Why Choose Us', icon: Building2, color: '#EC4899' },
  { key: 'call_to_action', label: 'Next Steps', icon: ArrowRight, color: '#10B981' },
];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Letterhead Settings Dialog ───
function LetterheadDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ company_name: '', company_info: '', accent_color: '#10B981', footer_text: '', logo_url: '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/email/letterhead`)
        .then(r => setForm(prev => ({ ...prev, ...r.data })))
        .catch(() => {});
    }
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/email/letterhead/upload-logo`, fd);
      setForm(prev => ({ ...prev, logo_url: data.logo_url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/email/letterhead`, form);
      toast.success('Letterhead saved');
      onSaved?.(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-emerald-600" /> Letterhead Settings</DialogTitle>
          <DialogDescription>Customize your proposal letterhead and branding</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs font-medium">Company Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="h-12 max-w-[120px] object-contain rounded border p-1" />
              ) : (
                <div className="h-12 w-20 rounded border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-gray-300" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Company Name</Label>
              <Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Acme Corp" data-testid="letterhead-company-name" />
            </div>
            <div>
              <Label className="text-xs font-medium">Accent Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.accent_color} onChange={e => setForm(p => ({ ...p, accent_color: e.target.value }))} className="w-10 h-9 rounded border cursor-pointer" />
                <Input value={form.accent_color} onChange={e => setForm(p => ({ ...p, accent_color: e.target.value }))} className="flex-1 font-mono text-xs" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Company Info</Label>
            <Input value={form.company_info} onChange={e => setForm(p => ({ ...p, company_info: e.target.value }))} placeholder="123 Main St, City | +1 (555) 000-0000 | info@company.com" data-testid="letterhead-company-info" />
          </div>
          <div>
            <Label className="text-xs font-medium">Footer Text</Label>
            <Input value={form.footer_text} onChange={e => setForm(p => ({ ...p, footer_text: e.target.value }))} placeholder="Thank you for your business!" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-letterhead-btn">
            {saving ? 'Saving...' : 'Save Letterhead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── AI Generation Dialog ───
function AIGenerateDialog({ open, onClose, onGenerated, clientName, clientCompany }) {
  const [form, setForm] = useState({ project_description: '', industry: '', tone: 'professional' });
  const [generating, setGenerating] = useState(false);
  const [selectedSections, setSelectedSections] = useState(SECTION_META.map(s => s.key));

  const toggleSection = (key) => {
    setSelectedSections(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleGenerate = async () => {
    if (!form.project_description.trim()) { toast.error('Describe the project'); return; }
    if (selectedSections.length === 0) { toast.error('Select at least one section'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/email/proposals/ai-generate`, {
        client_name: clientName,
        client_company: clientCompany,
        industry: form.industry,
        project_description: form.project_description,
        tone: form.tone,
        sections_to_generate: selectedSections,
      });
      if (data.parse_warning) toast.info('AI response was adapted. Review & edit sections.');
      else toast.success('Proposal sections generated!');
      onGenerated(data.sections);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-500" /> AI Proposal Generator</DialogTitle>
          <DialogDescription>Describe the project and let AI draft your proposal sections</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs font-medium">Project Description *</Label>
            <Textarea
              value={form.project_description}
              onChange={e => setForm(p => ({ ...p, project_description: e.target.value }))}
              placeholder="E.g., Complete website redesign for a luxury hotel chain, including booking engine, mobile-first design, SEO optimization, and CMS integration..."
              rows={4}
              data-testid="ai-project-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Industry</Label>
              <SearchableSelect
                value={form.industry}
                onValueChange={v => setForm(p => ({ ...p, industry: v }))}
                options={INDUSTRY_OPTIONS}
                placeholder="Select industry..."
                searchPlaceholder="Search industries..."
                triggerClassName="h-9 text-sm"
                data-testid="ai-industry"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Tone</Label>
              <Select value={form.tone} onValueChange={v => setForm(p => ({ ...p, tone: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="formal">Formal</SelectItem>
                  <SelectItem value="persuasive">Persuasive</SelectItem>
                  <SelectItem value="concise">Concise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium mb-2 block">Sections to Generate</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {SECTION_META.map(s => (
                <button
                  key={s.key}
                  onClick={() => toggleSection(s.key)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs border transition-all ${
                    selectedSections.includes(s.key) ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <s.icon className="h-3 w-3" style={{ color: selectedSections.includes(s.key) ? s.color : undefined }} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleGenerate} disabled={generating} className="bg-violet-600 hover:bg-violet-700 gap-1.5" data-testid="generate-ai-btn">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generating ? 'Generating...' : 'Generate Sections'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Proposal Editor Dialog ───
function ProposalEditorDialog({ open, onClose, onSaved, editing, leads }) {
  const [form, setForm] = useState({
    title: '', client_name: '', client_company: '', client_email: '', lead_id: '', total_value: 0, notes: '', status: 'draft'
  });
  const [sections, setSections] = useState({});
  const [activeSection, setActiveSection] = useState('hook');
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title || '', client_name: editing.client_name || '',
        client_company: editing.client_company || '', client_email: editing.client_email || '',
        lead_id: editing.lead_id || '', total_value: editing.total_value || 0,
        notes: editing.notes || '', status: editing.status || 'draft',
      });
      setSections(editing.sections || {});
    } else {
      setForm({ title: '', client_name: '', client_company: '', client_email: '', lead_id: '', total_value: 0, notes: '', status: 'draft' });
      setSections({});
    }
    setActiveSection('hook');
  }, [editing, open]);

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setForm(p => ({
        ...p, lead_id: leadId,
        client_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
        client_company: lead.company || '',
        client_email: lead.email || '',
      }));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Proposal title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, sections, total_value: parseFloat(form.total_value) || 0 };
      if (editing) {
        const { data } = await axios.put(`${API}/api/email/proposals/${editing.id}`, payload);
        toast.success('Proposal updated');
        onSaved(data, true);
      } else {
        const { data } = await axios.post(`${API}/api/email/proposals`, payload);
        toast.success('Proposal created');
        onSaved(data, false);
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAIGenerated = (generatedSections) => {
    setSections(prev => ({ ...prev, ...generatedSections }));
  };

  const handlePreview = async () => {
    // Fetch letterhead for professional preview
    let lh = {};
    try {
      const { data } = await axios.get(`${API}/api/email/letterhead`);
      lh = data;
    } catch {}
    const accentColor = lh.accent_color || '#10B981';
    const s = sections;
    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    let html = `<div style="max-width:700px;margin:0 auto;font-family:'Segoe UI',Arial,sans-serif;background:#fff;">`;
    // Letterhead header
    html += `<div style="padding:24px 30px;border-bottom:3px solid ${accentColor};display:flex;align-items:center;justify-content:space-between;">`;
    html += `<div>`;
    if (lh.logo_url) html += `<img src="${lh.logo_url}" alt="Logo" style="max-height:50px;max-width:160px;object-fit:contain;" />`;
    if (lh.company_name) html += `<p style="margin:4px 0 0;font-size:16px;font-weight:700;color:#111;">${lh.company_name}</p>`;
    if (lh.company_info) html += `<p style="margin:2px 0 0;font-size:11px;color:#888;">${lh.company_info}</p>`;
    html += `</div><div style="text-align:right;"><p style="margin:0;font-size:11px;color:#888;">Date: ${today}</p></div></div>`;
    // Title
    html += `<div style="padding:25px 30px 15px;">`;
    html += `<h1 style="font-size:22px;color:#111;margin:0 0 6px;font-weight:700;">${form.title || 'Untitled Proposal'}</h1>`;
    if (form.client_name) html += `<p style="color:#666;font-size:13px;margin:0;">Prepared for: <strong style="color:#333;">${form.client_name}</strong>${form.client_company ? ` at ${form.client_company}` : ''}</p>`;
    html += `</div>`;
    // Sections
    SECTION_META.forEach(({ key, label, color }) => {
      if (s[key]) {
        html += `<div style="padding:0 30px 18px;">`;
        html += `<h2 style="font-size:16px;color:${color};margin:0 0 8px;font-weight:600;padding-bottom:6px;border-bottom:1px solid #f0f0f0;">${label}</h2>`;
        html += `<div style="color:#444;font-size:14px;line-height:1.7;">${s[key]}</div>`;
        html += `</div>`;
      }
    });
    // Total value
    if (form.total_value > 0) {
      html += `<div style="margin:10px 30px 20px;padding:18px;background:linear-gradient(135deg,${accentColor}11,${accentColor}22);border:1px solid ${accentColor}33;border-radius:10px;text-align:center;">`;
      html += `<p style="margin:0;color:#666;font-size:11px;text-transform:uppercase;letter-spacing:1px;">Total Investment</p>`;
      html += `<p style="margin:6px 0 0;color:${accentColor};font-size:32px;font-weight:800;">${fc(form.total_value)}</p></div>`;
    }
    // Footer
    if (lh.footer_text) {
      html += `<div style="padding:16px 30px;border-top:1px solid #eee;text-align:center;"><p style="margin:0;font-size:11px;color:#999;">${lh.footer_text}</p></div>`;
    }
    html += `</div>`;
    setPreviewHtml(html);
    setPreviewOpen(true);
  };

  const filledSections = SECTION_META.filter(s => sections[s.key]?.trim()).length;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              {editing ? 'Edit Proposal' : 'Create Proposal'}
              {filledSections > 0 && <Badge variant="outline" className="ml-2 text-xs">{filledSections}/{SECTION_META.length} sections</Badge>}
            </DialogTitle>
            <DialogDescription>Build a professional proposal with AI assistance</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 mt-1 pr-1">
            {/* Client Info */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs">Proposal Title *</Label>
                <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Website Redesign Proposal" data-testid="proposal-title-input" />
              </div>
              <div>
                <Label className="text-xs">Total Value</Label>
                <Input type="number" min="0" step="100" value={form.total_value} onChange={e => setForm(p => ({ ...p, total_value: e.target.value }))} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs">Link to Contact</Label>
                <Select value={form.lead_id || "none"} onValueChange={v => v === "none" ? setForm(p => ({...p, lead_id: ''})) : handleLeadSelect(v)}>
                  <SelectTrigger data-testid="proposal-lead-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name} {l.company ? `(${l.company})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Client Name</Label>
                <Input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} placeholder="John Smith" data-testid="proposal-client-name" />
              </div>
              <div>
                <Label className="text-xs">Client Company</Label>
                <Input value={form.client_company} onChange={e => setForm(p => ({ ...p, client_company: e.target.value }))} placeholder="Acme Corp" />
              </div>
              <div>
                <Label className="text-xs">Client Email</Label>
                <Input type="email" value={form.client_email} onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))} placeholder="client@company.com" data-testid="proposal-client-email" />
              </div>
            </div>

            {/* AI Generate Button */}
            <div className="flex items-center gap-2 p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <Sparkles className="h-5 w-5 text-violet-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-violet-900">AI Proposal Generator</p>
                <p className="text-xs text-violet-600">Describe your project and let AI draft all sections</p>
              </div>
              <Button size="sm" onClick={() => setAiOpen(true)} className="bg-violet-600 hover:bg-violet-700 h-8 gap-1" data-testid="open-ai-generator-btn">
                <Sparkles className="h-3.5 w-3.5" /> Generate
              </Button>
            </div>

            {/* Section Editor */}
            <div className="border rounded-lg overflow-hidden">
              <div className="flex overflow-x-auto border-b bg-gray-50 px-1 py-1 gap-0.5 scrollbar-hide">
                {SECTION_META.map(s => (
                  <button
                    key={s.key}
                    onClick={() => setActiveSection(s.key)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs whitespace-nowrap transition-all ${
                      activeSection === s.key
                        ? 'bg-white text-gray-900 shadow-sm font-medium'
                        : sections[s.key] ? 'text-emerald-600 hover:bg-white/60' : 'text-gray-500 hover:bg-white/60'
                    }`}
                    data-testid={`section-tab-${s.key}`}
                  >
                    <s.icon className="h-3 w-3" style={{ color: s.color }} />
                    {s.label}
                    {sections[s.key] && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-0.5" />}
                  </button>
                ))}
              </div>
              <div className="p-3">
                <Textarea
                  value={sections[activeSection] || ''}
                  onChange={e => setSections(prev => ({ ...prev, [activeSection]: e.target.value }))}
                  placeholder={`Write or paste ${SECTION_META.find(s => s.key === activeSection)?.label} content here... HTML is supported.`}
                  rows={8}
                  className="font-mono text-xs"
                  data-testid="section-editor"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Internal Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes (not sent to client)" rows={2} />
            </div>
          </div>

          <DialogFooter className="mt-3 flex-shrink-0 gap-1.5">
            <Button variant="outline" size="sm" onClick={handlePreview} className="gap-1" data-testid="preview-proposal-btn"><Eye className="h-3.5 w-3.5" /> Preview</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="save-proposal-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIGenerateDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onGenerated={handleAIGenerated}
        clientName={form.client_name}
        clientCompany={form.client_company}
      />

      {/* Preview Dialog - Full Width Professional */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-blue-500" /> Professional Preview</DialogTitle>
            <DialogDescription>This is how the proposal will appear to your client</DialogDescription>
          </DialogHeader>
          <div className="border rounded-xl shadow-sm bg-white overflow-hidden" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Send Email Dialog ───
function SendEmailDialog({ open, onClose, proposal, onSent }) {
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!proposal) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/email/proposals/${proposal.id}/send`, {});
      toast.success(`Proposal sent to ${data.to}`);
      onSent(proposal.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-blue-500" /> Send Proposal</DialogTitle>
          <DialogDescription>Send this proposal as an HTML email via Resend</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="p-3 bg-gray-50 rounded-lg space-y-1.5">
            <p className="text-sm font-medium text-gray-900">{proposal?.title}</p>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Mail className="h-3 w-3" />
              <span>{proposal?.client_email || 'No email set'}</span>
            </div>
            {proposal?.total_value > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <DollarSign className="h-3 w-3" />
                <span>{fc(proposal.total_value)}</span>
              </div>
            )}
          </div>
          {!proposal?.client_email && (
            <p className="text-xs text-red-500">Client email is required. Edit the proposal first.</p>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || !proposal?.client_email} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="confirm-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Template Dialogs ───
function SaveTemplateDialog({ open, onClose, sections }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Template name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/email/proposal-templates`, {
        name, description, category, sections,
      });
      toast.success('Template saved');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-amber-500" /> Save as Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Template Name *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Web Design Proposal" data-testid="template-name-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description..." />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="web_design">Web Design</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="consulting">Consulting</SelectItem>
                <SelectItem value="technology">Technology</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-template-btn">
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ───
export default function ProposalCreatorPage() {
  const { format: fc } = useCurrency();
  const [proposals, setProposals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [letterheadOpen, setLetterheadOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendingProposal, setSendingProposal] = useState(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateSections, setTemplateSections] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    try {
      const [propRes, tplRes, leadRes] = await Promise.all([
        axios.get(`${API}/api/email/proposals`),
        axios.get(`${API}/api/email/proposal-templates`),
        axios.get(`${API}/api/leads`).catch(() => ({ data: [] })),
      ]);
      setProposals(propRes.data);
      setTemplates(tplRes.data);
      setLeads(leadRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaved = (proposal, isUpdate) => {
    if (isUpdate) setProposals(prev => prev.map(p => p.id === proposal.id ? proposal : p));
    else setProposals(prev => [proposal, ...prev]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this proposal?')) return;
    try {
      await axios.delete(`${API}/api/email/proposals/${id}`);
      setProposals(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSent = (pid) => {
    setProposals(prev => prev.map(p => p.id === pid ? { ...p, status: 'sent' } : p));
  };

  const handleLoadTemplate = (tpl) => {
    setEditing({ title: `${tpl.name} - New`, sections: tpl.sections, client_name: '', client_email: '', client_company: '', status: 'draft', total_value: 0, notes: '' });
    setEditorOpen(true);
  };

  const handleDeleteTemplate = async (tid) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/api/email/proposal-templates/${tid}`);
      setTemplates(prev => prev.filter(t => t.id !== tid));
      toast.success('Template deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = statusFilter === 'all' ? proposals : proposals.filter(p => p.status === statusFilter);
  const totalValue = proposals.reduce((s, p) => s + (p.total_value || 0), 0);
  const sentCount = proposals.filter(p => p.status === 'sent').length;
  const acceptedValue = proposals.filter(p => p.status === 'accepted').reduce((s, p) => s + (p.total_value || 0), 0);

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="proposal-creator-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-violet-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Proposal Creator<PageInfoTooltip pageKey="proposal-creator" /></h1>
            <p className="text-xs text-gray-400">
              {proposals.length} proposals · {fc(totalValue)} pipeline · {fc(acceptedValue)} won
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setLetterheadOpen(true)} className="h-8 gap-1 text-xs" data-testid="letterhead-settings-btn">
            <Settings2 className="h-3.5 w-3.5" /> Letterhead
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setEditorOpen(true); }} className="h-8 bg-violet-600 hover:bg-violet-700 gap-1 text-xs" data-testid="create-proposal-btn">
            <Plus className="h-3.5 w-3.5" /> New Proposal
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: proposals.length, icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50' },
          { label: 'Draft', value: proposals.filter(p => p.status === 'draft').length, icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Sent', value: sentCount, icon: Send, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Won', value: proposals.filter(p => p.status === 'accepted').length, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(s => (
          <Card key={s.label} className="border-gray-200">
            <CardContent className="p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{s.value}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs: Proposals + Templates */}
      <Tabs defaultValue="proposals" className="w-full">
        <TabsList className="overflow-x-auto flex-nowrap w-full sm:w-auto">
          <TabsTrigger value="proposals" className="gap-1" data-testid="tab-proposals"><FileText className="h-3.5 w-3.5" /> Proposals</TabsTrigger>
          <TabsTrigger value="templates" className="gap-1" data-testid="tab-templates"><LayoutTemplate className="h-3.5 w-3.5" /> Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="proposals" className="mt-4">
          {/* Filter */}
          <div className="flex items-center gap-2 mb-3 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1">
            {['all', ...STATUSES.map(s => s.value)].map(v => (
              <button
                key={v}
                onClick={() => setStatusFilter(v)}
                className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                  statusFilter === v ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${v}`}
              >
                {v === 'all' ? 'All' : STATUSES.find(s => s.value === v)?.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-violet-500 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="p-10 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 mb-1">{statusFilter === 'all' ? 'No proposals yet' : `No ${statusFilter} proposals`}</h3>
                <p className="text-sm text-gray-500 mb-4">Create your first professional proposal with AI assistance</p>
                <Button onClick={() => { setEditing(null); setEditorOpen(true); }} className="bg-violet-600 hover:bg-violet-700 gap-1.5">
                  <Plus className="h-4 w-4" /> New Proposal
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2.5">
              {filtered.map(p => {
                const statusInfo = STATUSES.find(s => s.value === p.status) || STATUSES[0];
                const expanded = expandedId === p.id;
                const filledSections = SECTION_META.filter(s => p.sections?.[s.key]?.trim()).length;
                return (
                  <Card key={p.id} className="border-gray-200 hover:shadow-sm transition-shadow" data-testid={`proposal-card-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : p.id)}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-bold text-gray-900 truncate">{p.title}</h3>
                            <Badge className={`${statusInfo.color} text-[10px]`}>{statusInfo.label}</Badge>
                            {filledSections > 0 && (
                              <span className="text-[10px] text-gray-400">{filledSections} sections</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {p.client_name || 'No client'}{p.client_company ? ` · ${p.client_company}` : ''}
                            {p.created_at ? ` · ${formatDate(p.created_at)}` : ''}
                          </p>
                        </div>
                        {p.total_value > 0 && <p className="text-lg font-bold text-gray-900">{fc(p.total_value)}</p>}
                        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                      {expanded && (
                        <div className="mt-3 pt-3 border-t space-y-3">
                          {/* Section pills */}
                          {filledSections > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {SECTION_META.filter(s => p.sections?.[s.key]?.trim()).map(s => (
                                <span key={s.key} className="px-2 py-0.5 rounded-full bg-gray-100 text-[10px] text-gray-600 flex items-center gap-1">
                                  <s.icon className="h-2.5 w-2.5" style={{ color: s.color }} /> {s.label}
                                </span>
                              ))}
                            </div>
                          )}
                          {p.notes && <p className="text-xs text-gray-500 italic">{p.notes}</p>}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); setEditing(p); setEditorOpen(true); }} data-testid={`edit-proposal-${p.id}`}>
                              <Pencil className="h-3 w-3" /> Edit
                            </Button>
                            {p.client_email && p.status === 'draft' && (
                              <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={(e) => { e.stopPropagation(); setSendingProposal(p); setSendOpen(true); }} data-testid={`send-proposal-${p.id}`}>
                                <Send className="h-3 w-3" /> Send Email
                              </Button>
                            )}
                            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); setTemplateSections(p.sections || {}); setSaveTemplateOpen(true); }}>
                              <Copy className="h-3 w-3" /> Save as Template
                            </Button>
                            {p.share_token && (
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-blue-600" onClick={(e) => {
                                e.stopPropagation();
                                const url = `${window.location.origin}/proposal/${p.share_token}`;
                                navigator.clipboard.writeText(url).then(() => toast.success('Share link copied!'));
                              }} data-testid={`share-proposal-${p.id}`}>
                                <Link2 className="h-3 w-3" /> Share Link
                              </Button>
                            )}
                            <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:bg-red-50 ml-auto" onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} data-testid={`delete-proposal-${p.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          {templates.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="p-8 text-center">
                <LayoutTemplate className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-gray-700 mb-1">No templates yet</h3>
                <p className="text-sm text-gray-500">Create a proposal and save it as a template to reuse later</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map(t => {
                const sectionCount = Object.keys(t.sections || {}).filter(k => t.sections[k]?.trim()).length;
                return (
                  <Card key={t.id} className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer" data-testid={`template-card-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate">{t.name}</h3>
                          {t.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t.description}</p>}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                            <span className="text-[10px] text-gray-400">{sectionCount} sections</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-3 pt-2 border-t">
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => handleLoadTemplate(t)}>
                          <FileText className="h-3 w-3" /> Use Template
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:bg-red-50" onClick={() => handleDeleteTemplate(t.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProposalEditorDialog open={editorOpen} onClose={() => { setEditorOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} leads={leads} />
      <LetterheadDialog open={letterheadOpen} onClose={() => setLetterheadOpen(false)} />
      <SendEmailDialog open={sendOpen} onClose={() => { setSendOpen(false); setSendingProposal(null); }} proposal={sendingProposal} onSent={handleSent} />
      <SaveTemplateDialog open={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} sections={templateSections} />
    </div>
  );
}
